import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CustomModal from "components/Shared/CustomModal/CustomModal.js";
import CompanyRow from "./CompanyRow";

export default function BreakdownModal({ open, handleClose, data, title, loading, navigationPath, history }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [groupedData, setGroupedData] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (open && data) {
      // Data structure from API: { total: number, companys: [...] }
      // Note: API uses "companys" not "companies"
      if (data.companys && Array.isArray(data.companys)) {
        // Process the companys array
        processGroupedData(data.companys);
        // Set total from the data object
        setTotal(data.total || 0);
      } else if (Array.isArray(data) && data.length > 0) {
        // Fallback: if data is directly an array of companies
        processGroupedData(data);
      } else {
        setGroupedData([]);
        setTotal(data.total || 0);
      }
    } else if (open && !data) {
      setGroupedData([]);
      setTotal(0);
    }
  }, [open, data]);

  const groupFlatData = (items) => {
    const grouped = {};
    let grandTotal = 0;

    items.forEach((item) => {
      const companyName = item.company_name || "Unknown Company";
      const branchName = item.branch_name || "Unknown Branch";
      const companyId = item.company_id || companyName;
      const branchId = item.branch_id || branchName;

      if (!grouped[companyId]) {
        grouped[companyId] = {
          id: companyId,
          name: companyName,
          total: 0,
          branches: {},
        };
      }

      if (!grouped[companyId].branches[branchId]) {
        grouped[companyId].branches[branchId] = {
          id: branchId,
          name: branchName,
          total: 0,
        };
      }

      grouped[companyId].total += 1;
      grouped[companyId].branches[branchId].total += 1;
      grandTotal += 1;
    });

    const companies = Object.values(grouped).map((company) => ({
      ...company,
      branches: Object.values(company.branches),
    }));

    setGroupedData(companies);
    setTotal(grandTotal);
  };

  const processGroupedData = (companysArray) => {
    // Process the companys array from API
    // Structure: [{ company_id, company_name, total, branches: [{ branch_id, branch_name, total }] }]
    if (Array.isArray(companysArray)) {
      const companies = companysArray.map((item) => ({
        id: item.company_id || item.id || Math.random(),
        name: item.company_name || item.name,
        total: item.total || 0,
        branches: Array.isArray(item.branches)
          ? item.branches.map((branch) => ({
              id: branch.branch_id || branch.id || Math.random(),
              name: branch.branch_name || branch.name,
              total: branch.total || 0,
            }))
          : [],
      }));
      
      setGroupedData(companies);
    } else {
      setGroupedData([]);
      setTotal(0);
    }
  };

  return (
    <CustomModal
      open={open}
      handleClose={handleClose}
      customStyle={{ 
        minWidth: isMobile ? "90vw" : "800px", 
        maxWidth: isMobile ? "90vw" : "90vw", 
        width: isMobile ? "90vw" : "auto",
        height: isMobile ? "90vh" : "auto",
        maxHeight: isMobile ? "90vh" : "90vh", 
        overflow: "hidden",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Box
        sx={{
          padding: isMobile ? "5px" : "20px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            flexShrink: 0,
          }}
        >
          <Typography variant="h5">
            {title}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "40px", flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexShrink: 0 }}>
              <Typography variant="h6" style={{ color: "#666" }}>
                Total: {total}
              </Typography>
              {navigationPath && history && (
                <Button
                  variant="outlined"
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => {
                    handleClose();
                    history.push(navigationPath);
                  }}
                  sx={{ textTransform: "none" }}
                >
                  View Details
                </Button>
              )}
            </Box>
            {groupedData && groupedData.length > 0 ? (
              <TableContainer 
                component={Paper}
                sx={{
                  maxHeight: "calc(90vh - 200px)",
                  overflowY: "auto",
                  overflowX: "auto",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f1f1",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888",
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: "#555",
                    },
                  },
                }}
              >
                <Table aria-label="company breakdown table" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Company Name</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedData.map((company) => (
                      <CompanyRow
                        key={company.id}
                        company={company}
                        branches={company.branches || []}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography sx={{ flexShrink: 0 }}>No data available</Typography>
            )}
          </Box>
        )}
      </Box>
    </CustomModal>
  );
}
